<template>
  <v-card v-if="modelValue.securityAwarenessConfigDto" class="mt-4">
    <v-card-text>
      <div class="subtitle1 mb-4">
        {{ $t("emailSettings.settingsTab.securityAwarenessTraining") }}
      </div>
      <div class="d-flex align-center security-awareness-alert">
        <v-icon icon="$warning" class="ml-2 mr-2"></v-icon>
        <span v-html="$t('emailSettings.settingsTab.securityAwarenessTrainingDisclaimer')"></span>
      </div>
      <v-checkbox
        v-model="localValue.securityAwarenessConfigDto.enabled"
        :ripple="false"
        :disabled="disabled"
      >
        <template #label>
          <div class="body2">
            {{ $t("emailSettings.settingsTab.securityAwarenessTrainingSubtitle") }}
          </div>
        </template>
      </v-checkbox>

      <div class="d-flex justify-space-between align-center mt-6">
        <div>
          <span class="subtitle1">{{ $t("emailSettings.settingsTab.allowList") }}</span>
          <span
            class="subtitle1"
            data-testid="security-awareness-section-count"
            v-if="localValue.securityAwarenessConfigDto.providerConfigurations.length > 0"
            >({{ localValue.securityAwarenessConfigDto.providerConfigurations.length }})</span
          >
        </div>

        <v-menu>
          <template #activator="{ props }">
            <v-btn
              class="add-btn"
              color="primary"
              rounded
              density="default"
              size="large"
              :disabled="disabled || !localValue.securityAwarenessConfigDto.enabled"
              v-bind="props"
              @click="
                onActionClick({
                  action: EmailSettingsAction.ADD_SECURITY_AWARENESS_CONFIGURATION,
                })
              "
            >
              <v-icon class="mr-2" icon="$add" />
              {{ $t("general.add") }}
            </v-btn>
          </template>
        </v-menu>
      </div>
      <email-security-security-awareness-table
        data-testid="security-awareness-table"
        v-if="localValue.securityAwarenessConfigDto.providerConfigurations.length"
        :disabled="disabled || !localValue.securityAwarenessConfigDto.enabled"
        :items="localValue.securityAwarenessConfigDto.providerConfigurations"
        @on-action-click="onActionClick($event)"
      />
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, toRefs, type PropType } from "vue";
import type {
  EmailSecuritySettings,
  ProviderConfiguration,
} from "@/_store/email-security/email-settings.module.ts";
import { EmailSettingsAction } from "@/constants/emails.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { ModalWidth } from "@/constants/modals.ts";
import AddSecurityAwarnessConfiguration from "@/components/modals/email-settings/AddSecurityAwarnessConfiguration.vue";
import EmailSecuritySecurityAwarenessTable from "@/components/tables/EmailSecuritySecurityAwarenessTable.vue";
import { useI18n } from "vue-i18n";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";
import { useTwoWayBinding } from "@/composables/useTwoWayBinding.ts";

export default defineComponent({
  components: {
    EmailSecuritySecurityAwarenessTable,
  },
  props: {
    modelValue: {
      type: Object as PropType<EmailSecuritySettings>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const { openDialog } = useDialogsStore();
    const { t } = useI18n();

    const { modelValue } = toRefs(props);
    const localValue = useTwoWayBinding(modelValue, (value) => {
      emit("update:modelValue", value);
    });

    const onActionClick = ({
      action,
      item,
      index,
    }: {
      item?: ProviderConfiguration;
      index?: number;
      action: EmailSettingsAction;
    }) => {
      switch (action) {
        case EmailSettingsAction.ADD_SECURITY_AWARENESS_CONFIGURATION:
          openDialog({
            ...componentDialogsConfigConstructor({
              callback: (val) =>
                localValue.value!.securityAwarenessConfigDto.providerConfigurations.push(val),
              width: ModalWidth.MEDIUM,
              component: AddSecurityAwarnessConfiguration,
              action,
            }),
            disable: props.disabled,
          });
          break;
        case EmailSettingsAction.EDIT_SECURITY_AWARENESS_CONFIGURATION:
          openDialog({
            ...componentDialogsConfigConstructor({
              callback: (val) =>
                (localValue.value!.securityAwarenessConfigDto.providerConfigurations[index!] = {
                  ...val,
                }),
              width: ModalWidth.MEDIUM,
              item,
              component: AddSecurityAwarnessConfiguration,
              action,
            }),
            disable: props.disabled,
          });
          break;
        case EmailSettingsAction.REMOVE_SECURITY_AWARENESS_CONFIGURATION:
          openDialog({
            ...confirmationDialogsConfigConstructor({
              item: {},
              action: EmailSettingsAction.REMOVE_SECURITY_AWARENESS_CONFIGURATION,
              callback: () => {
                if (localValue.value) {
                  localValue.value.securityAwarenessConfigDto.providerConfigurations.splice(
                    index!,
                    1
                  );
                }
              },
            }),
            header: {
              title: t(
                `modals.${EmailSettingsAction.REMOVE_SECURITY_AWARENESS_CONFIGURATION}.title`
              ),
              titleClass: "text-red-dark",
              titleIcon: "warning",
            },
            disable: props.disabled,
          });
          break;
      }
    };

    return {
      localValue,
      onActionClick,
      EmailSettingsAction,
    };
  },
});
</script>

<style scoped lang="scss">
.security-awareness-alert {
  height: 40px;
  color: rgb(var(--v-theme-primary));
  background-color: rgb(var(--v-theme-indigo-pale));

  .v-icon:before {
    color: rgb(var(--v-theme-primary)) !important;
  }
}
</style>
